import { LockOutlined } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { EvaluationMark, EvaluationStatus } from '@types';
import { getHappinessColor } from '@utils';
import { useNavigate } from 'react-router-dom';

type Props = {
  monthLabel: string;
  feedbackSummary?: EvaluationMark;
  status: EvaluationStatus;
  isLocked?: boolean;
  isJoinEvent?: boolean;
  evaluationId?: string;
  providedByName?: string;
};

const getColor = (feedback?: EvaluationMark, status?: EvaluationStatus) => {
  const lowerCasedFeedback = feedback ? feedback?.toLocaleLowerCase() : '';
  if (status === 'upcoming') return 'text.link';
  if (status === 'cancelled') return 'grey.100';
  if (!feedback) return 'grey.300';
  if (lowerCasedFeedback === 'exceeds') return getHappinessColor(10);
  if (lowerCasedFeedback === 'successful') return getHappinessColor(8);
  if (lowerCasedFeedback === 'needs development') return getHappinessColor(4);
  return 'grey.400';
};

export const EvaluationTile = ({
  monthLabel,
  feedbackSummary,
  status,
  isLocked = false,
  isJoinEvent = false,
  evaluationId,
  providedByName,
}: Props) => {
  const color = getColor(feedbackSummary, status);
  const isUpcoming = status === 'upcoming';
  const navigate = useNavigate();
  const handleClick = () => {
    if (isLocked || !evaluationId || status !== 'closed') {
      return;
    }
    navigate(`./${evaluationId}`);
  };

  return (
    <Box
      height={{ tablet: 119 }}
      width={{ tablet: 200 }}
      onClick={handleClick}
      sx={{
        cursor:
          (!isLocked && evaluationId && status === 'closed') ||
          status === 'ongoing'
            ? 'pointer'
            : 'default',
        borderRadius: 2,
        backgroundColor: color,
        color: status === 'cancelled' ? 'text.secondary' : 'common.white',
        outline: status === 'upcoming' ? 'white dashed 1px' : 'none',
        outlineOffset: status === 'upcoming' ? '-6px' : 0,
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 18 }}>{monthLabel}</Typography>
        <Typography sx={{ fontSize: 13 }}>
          {isJoinEvent && 'Joined your project'}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          {isUpcoming && 'Upcoming evaluation'}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={0.5}>
        {isLocked && (
          <Tooltip title={`Provided by ${providedByName}`} placement="top">
            <LockOutlined sx={{ fontSize: 16 }} />
          </Tooltip>
        )}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'normal',
            fontSize: '14px',
            textTransform: 'capitalize',
          }}
        >
          {isJoinEvent || isUpcoming
            ? ''
            : feedbackSummary
              ? feedbackSummary
              : 'No feedback'}
        </Typography>
      </Box>
    </Box>
  );
};
